import { KeyboardArrowDown } from '@mui/icons-material';
import { Divider, ListItemSecondaryAction } from '@mui/material';
import List from '@mui/material/List';
import classNames from 'classnames';
import React, { cloneElement, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DrawerItem } from '~/Components';
import { BADGE_NEW, ICON_SECONDARY, LABEL_COLOR, WHITE } from '~/Constants/Colors.constants';
import { FONT_SIZE_SMALL } from '~/Constants/UI.constants';
import { makeStyles } from '~/UIKit/Style';
import { IMenuItems } from '..';

const useStyles = makeStyles((theme) => ({
  icon: {
    color: ICON_SECONDARY,
  },
  subMenuWrapper: {
    display: 'block',
    paddingLeft: theme.spacing(6),
  },
  sectionHeader: {
    paddingLeft: theme.spacing(2),
    paddingTop: '16px',
    textTransform: 'uppercase',
    fontWeight: 'bold',
    lineHeight: '0px',
    fontSize: FONT_SIZE_SMALL,
    color: LABEL_COLOR,
    letterSpacing: '1px',
    position: 'relative',
  },
  listPadding: {
    padding: 0,
  },
  badgeNew: {
    top: -7,
    right: 16,
    color: WHITE,
    lineHeight: 1.2,
    borderRadius: 3,
    padding: '0 8px',
    fontWeight: 'bold',
    position: 'absolute',
    fontSize: FONT_SIZE_SMALL,
    textTransform: 'uppercase',
    background: BADGE_NEW,
    letterSpacing: 0,
  },
  arrowIcon: {
    paddingTop: 5,
    cursor: 'pointer',
    color: ICON_SECONDARY,
  },
}));

export interface IDrawerItemsProps {
  menuItems: IMenuItems[];
}

export const DrawerItems = ({ menuItems }: IDrawerItemsProps) => {
  const { classes } = useStyles();
  const loc = useLocation();
  const pathname = loc?.pathname?.replace('/edit', '')?.replace('/create', '');

  const [expanded, setExpand] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  return (
    <List classes={{ padding: classes.listPadding }}>
      {menuItems?.map((item, index) => {
        const isActive = Array.isArray(item.matchURL)
          ? item.matchURL.includes(pathname)
          : item?.matchURL === pathname;
        const hasSub = !!item?.subMenus;
        const isSubVisible =
          hasSub && !!item.subMenus?.filter((subMenu) => subMenu.hasAccess)?.length;

        const isVisible = item.hasAccess && (!hasSub || isSubVisible);
        return (
          isVisible && (
            <React.Fragment key={`DRAWER_ITEM_${index}`}>
              {item.hasDivider && <Divider />}
              {item.groupLabel && <p className={classes.sectionHeader}>{item.groupLabel}</p>}
              <DrawerItem
                icon={
                  item?.icon &&
                  cloneElement(item.icon as React.ReactElement, {
                    className: classes.icon,
                  })
                }
                onClick={hasSub ? () => setExpand(!expanded) : item.onClick}
                testId={item.testId}
                text={item.text}
                isHovered={hoveredIndex === index}
                secondaryIcon={
                  hasSub && (
                    <ListItemSecondaryAction
                      onClick={() => setExpand(!expanded)}
                      className={classes.arrowIcon}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                    >
                      <KeyboardArrowDown
                        sx={{ transform: expanded ? 'rotate(-180deg)' : 'initial' }}
                      />
                    </ListItemSecondaryAction>
                  )
                }
                isActive={isActive}
                isNew={item.isNew}
              />
              {hasSub && expanded && (
                <div id={`subMenu-${index}`} className={classNames(classes.subMenuWrapper)}>
                  {item.subMenus
                    ?.filter((subMenu) => subMenu.hasAccess)
                    .map((subMenu) => (
                      <DrawerItem
                        isSubMenu
                        key={index}
                        icon={
                          subMenu?.icon &&
                          cloneElement(subMenu.icon as React.ReactElement, {
                            className: classes.icon,
                          })
                        }
                        onClick={subMenu.onClick}
                        text={subMenu.text}
                        isNew={subMenu.isNew}
                        isActive={subMenu?.matchURL === pathname}
                      />
                    ))}
                </div>
              )}
            </React.Fragment>
          )
        );
      })}
    </List>
  );
};
